import { useState } from "react";

import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { IconButton } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import HighlightTerm from "app/components/highlight-term/HighlightTerm";

import type { ENTITY_ICONS, AutocompleteDemandAnalysisItem } from "../types/AutocompleteDemandAnalysisTypes";

const StyledIcon = styled(IconButton)`
    ${SWReactIcons} {
        svg {
            path {
                fill-opacity: 1;
                fill: ${colorsPalettes.carbon[300]};
            }
        }
    }
`;

type ActionButtonProps = {
    iconName: "social-share" | "edit-icon";
    onClick: (e: React.MouseEvent) => void;
    dataAutomation: string;
};

const ActionButton: React.FC<ActionButtonProps> = ({ iconName, onClick, dataAutomation }) => (
    <StyledIcon
        iconSize="xs"
        type="flat"
        iconName={iconName}
        onClick={onClick}
        dataAutomation={dataAutomation}
        iconColor={colorsPalettes.carbon[300]}
    />
);

type AutocompleteDemandAnalysisListItemProps = {
    query: string;
    title: string;
    subtitle?: string;
    isActive?: boolean;
    onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
    iconName: (typeof ENTITY_ICONS)[keyof typeof ENTITY_ICONS];
    item?: AutocompleteDemandAnalysisItem;
    onEditClick?: (item: AutocompleteDemandAnalysisItem) => void;
    onShareClick?: (item: AutocompleteDemandAnalysisItem) => void;
    showEditButton?: boolean;
    showShareButton?: boolean;
};

export const AutocompleteDemandAnalysisListItem: React.FC<AutocompleteDemandAnalysisListItemProps> = ({
    query,
    title,
    subtitle,
    isActive = false,
    onClick,
    iconName,
    item,
    onEditClick,
    onShareClick,
    showEditButton = false,
    showShareButton = false,
}) => {
    const [isHovered, setIsHovered] = useState(false);

    const isKeywordGroup = item && (item.type === "ownList" || item.type === "sharedList");
    const canEdit = item && item.type === "ownList";
    const canShare = item && item.type === "ownList";
    const shouldShowActions =
        isKeywordGroup && (isHovered || isActive) && ((showEditButton && canEdit) || (showShareButton && canShare));

    const handleEditClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (item && onEditClick) {
            onEditClick(item);
        }
    };

    const handleShareClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (item && onShareClick) {
            onShareClick(item);
        }
    };

    return (
        <Flex
            cursor="pointer"
            padding="6px 16px"
            alignItems="center"
            onClick={onClick}
            background={isActive ? colorsPalettes.carbon[25] : "transparent"}
            data-automation={`AutocompleteDemandAnalysisListItem-${item?.type}`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            position="relative"
            whenHover={{
                background: colorsPalettes.carbon[25],
            }}
        >
            <Flex
                width="32px"
                height="32px"
                padding="8px"
                alignItems="center"
                justifyContent="center"
                margin="0 8px 0 0"
                border={`1px solid ${colorsPalettes.carbon[50]}`}
                borderRadius="6px"
                background={colorsPalettes.carbon[0]}
            >
                <SWReactIcons
                    size="xs"
                    iconName={iconName}
                    iconColor={colorsPalettes.carbon[400]}
                />
            </Flex>
            <Flex
                flexDirection="column"
                justifyContent="center"
                width="100%"
            >
                <Text
                    fontVariant="input2"
                    color={colorsPalettes.carbon[500]}
                >
                    <HighlightTerm
                        sentence={title}
                        highlightTerm={query}
                    />
                    {subtitle ? (
                        <Text
                            fontVariant="listItem3"
                            color={colorsPalettes.carbon[300]}
                            data-automation={item?.type}
                        >
                            {" • "}
                            {subtitle}
                        </Text>
                    ) : null}
                </Text>
            </Flex>
            {shouldShowActions ? (
                <Flex alignItems="center">
                    {showShareButton && canShare ? (
                        <ActionButton
                            iconName="social-share"
                            onClick={handleShareClick}
                            dataAutomation="autocomplete-share-button"
                        />
                    ) : null}
                    {showEditButton && canEdit ? (
                        <ActionButton
                            iconName="edit-icon"
                            onClick={handleEditClick}
                            dataAutomation="autocomplete-edit-button"
                        />
                    ) : null}
                </Flex>
            ) : null}
        </Flex>
    );
};
